import { FC, ReactElement, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { ValuesType } from 'utility-types';
import { UseSelectStateChange } from 'downshift';
import cn from 'classnames';
import { uniqueId } from 'lodash';
import { useMediaQuery } from 'usehooks-ts';
import { IconDefinition, IconProp } from '@fortawesome/fontawesome-svg-core';
import { faAngleDown } from '@fortawesome/pro-light-svg-icons';
import {
  Button,
  createBemBlockBuilder,
  Dropdown as EbscoDropdown,
} from '@ebsco-ui/ebsco-ui';

import { sharedMessages } from '@app/translations';
import { breakpoint } from '@app/constants';

import './Dropdown.scss';

type EbscoDropdownProps = Omit<Parameters<typeof EbscoDropdown>[0], 'children'>;

export type DropdownItem = ValuesType<EbscoDropdownProps['items']>;

type DropdownProps = Partial<EbscoDropdownProps> & {
  selectedItem?: DropdownItem;
  id?: string;
  label?: string;
  hideLabel?: boolean;
  toggleIcon?: IconDefinition;
  toggleIconOnly?: boolean;
  isLoading?: boolean;
  placeholder?: string;
  CustomToggle?: (getToggleButtonProps) => ReactElement;
  onSelectedItemChange?: ({
    selectedItem,
  }: UseSelectStateChange<DropdownItem>) => void;
};

const cnBem = createBemBlockBuilder(['dropdown']);

export const Dropdown: FC<DropdownProps> = ({
  id = uniqueId('dropdown-'),
  selectedItem,
  items = [],
  label,
  hideLabel,
  toggleIcon,
  toggleIconOnly,
  className,
  styleType,
  variant,
  CustomToggle,
  isLoading,
  placeholder,
  onSelectedItemChange,
}) => {
  const { $t } = useIntl();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const defaultSelectedItem = () => ({ label: '' });
  const isMobile = useMediaQuery(
    `(max-width: ${breakpoint.SCREEN.TABLET_LG - 1}px)`
  );

  const onIsOpenChange = changes => {
    setIsDropdownOpen(changes.isOpen);
  };

  const Toggle = useCallback(
    ({ getToggleButtonProps, ...rest }) => {
      const icon = (selectedItem?.icon ?? toggleIcon) as IconProp;

      return (
        <Button type="button" {...getToggleButtonProps(rest)}>
          {icon && <EbscoDropdown.Icon icon={icon} />}
          {!toggleIconOnly && (
            <>
              <span className="eb-dropdown__toggle-button-label">
                {selectedItem?.mobileLabel && isMobile
                  ? selectedItem?.mobileLabel
                  : selectedItem?.label}
              </span>
              <EbscoDropdown.Icon icon={faAngleDown} />
            </>
          )}
        </Button>
      );
    },
    [
      isMobile,
      selectedItem?.icon,
      selectedItem?.label,
      selectedItem?.mobileLabel,
      toggleIcon,
      toggleIconOnly,
    ]
  );

  return (
    <EbscoDropdown
      items={items}
      className={cn(cnBem(), className)}
      hideLabel={hideLabel}
      downshiftOptions={{
        selectedItem: !isLoading
          ? items.find(item => item.label === selectedItem?.label) ??
            defaultSelectedItem
          : defaultSelectedItem,
        onSelectedItemChange,
        onIsOpenChange,
      }}
      variant={variant}
      styleType={styleType}
      isLoading={isLoading}
      placeholder={placeholder}
      loadingPlaceholder={$t(sharedMessages.loadingMessage)}
    >
      <EbscoDropdown.Context>
        {({ getToggleButtonProps }) => (
          <>
            <div className="dropdown-control" id={id}>
              {label && <EbscoDropdown.Label>{label}</EbscoDropdown.Label>}
              {selectedItem?.icon || selectedItem?.mobileLabel || toggleIcon ? (
                <Toggle getToggleButtonProps={getToggleButtonProps} />
              ) : CustomToggle ? (
                <CustomToggle getToggleButtonProps={getToggleButtonProps} />
              ) : (
                <EbscoDropdown.Toggle />
              )}
            </div>
            <EbscoDropdown.Menu
              aria-labelledby={id}
              aria-hidden={!isDropdownOpen}
            >
              {items.map((item, index) => (
                <EbscoDropdown.Item
                  key={`${item.label}-${index}`}
                  item={item}
                  index={index}
                >
                  {item.icon && <EbscoDropdown.Icon icon={item.icon} />}
                  {item.label}
                </EbscoDropdown.Item>
              ))}
            </EbscoDropdown.Menu>
          </>
        )}
      </EbscoDropdown.Context>
    </EbscoDropdown>
  );
};
